import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

/** One address, the way the server reads it (utils/emailList). */
export const looksLikeEmail = (s: string) => /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(String(s || '').trim());

/**
 * The tick list on Send: every address on the account, ticked the way the
 * server suggests (a bid: everyone; a bill: the AP side). Untick anyone before
 * sending. `onChange` gets the ticked addresses, comma-joined — what the send
 * routes take as `to`. One address: nothing to choose, nothing shown, and
 * null — the server sends to its usual address.
 *
 * Nobody on file (owner, Oct 7 2026: "if I click send Via email and they dont
 * have an email allow me to put one in and save that to client card"): a box to
 * type one. `onChange` gets what is typed, `onMissing(true)` tells the sheet so
 * it sends `save_email` with it — the send route puts it on the client's card.
 */
export default function EmailPicker({ url, onChange, onMissing, who }: {
  url: string; onChange: (to: string | null) => void; onMissing?: (missing: boolean) => void; who?: string;
}) {
  const [list, setList] = useState<{ email: string; label: string; on: boolean }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    let live = true;
    setLoaded(false);
    // A list that could not be read is not "no email on file": show nothing, the server sends to its usual address.
    api.get(url).then(r => { if (live) { setList(r.data?.recipients || []); setLoaded(true); } }).catch(() => { if (live) setList([]); });
    return () => { live = false; };
  }, [url]);
  const none = loaded && list.length === 0;
  useEffect(() => { onMissing?.(none); }, [none]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    onChange(none ? (typed.trim() || null) : list.length < 2 ? null : list.filter(r => r.on).map(r => r.email).join(', '));
  }, [list, none, typed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (none) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
        <label className="block text-xs font-bold uppercase tracking-wide text-amber-800">
          No email on file{who ? ` for ${who}` : ''}
          <input type="email" inputMode="email" autoComplete="off" autoCapitalize="none" value={typed} onChange={e => setTyped(e.target.value)}
            placeholder="Type their email" className="input mt-1 w-full normal-case font-normal text-[16px]" />
        </label>
        <p className="text-xs text-amber-900/80 mt-1">It is saved to their client card when you tap Send via Email.</p>
      </div>
    );
  }
  if (list.length < 2) return null;
  const flip = (i: number) => setList(prev => prev.map((r, j) => (j === i ? { ...r, on: !r.on } : r)));
  return (
    <div className="rounded-lg border border-gray-200 px-3 py-2">
      <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Email to</p>
      {list.map((r, i) => (
        <label key={r.email} className="flex items-center gap-2 py-0.5 text-sm text-gray-800">
          <input type="checkbox" checked={r.on} onChange={() => flip(i)} className="h-4 w-4" />
          <span className="font-semibold">{r.label}</span>
          <span className="text-gray-600 break-all">{r.email}</span>
        </label>
      ))}
    </div>
  );
}
